import { createClient } from '@connectrpc/connect';
import { RevitService } from './gen/revit_service_pb';
import { getGrpcTransport } from './transport';
import { getGrpcBaseUrl, getGrpcPort, subscribeGrpcTargetChange } from './grpcRuntime';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import { webLog } from './webLogger';
import { DEVICE_VERIFICATION_ERROR, PLUGIN_UPDATE_ERROR, normalizeMachineFingerprint } from '../../lib/machineFingerprint';

type PingResult = { success: boolean; error?: string };
type ConnectResult = { success: boolean; fingerprint?: string; error?: string };

const PING_CACHE_TTL_MS = 15_000;
const CONNECT_CACHE_TTL_MS = 60_000;
let lastPingResult: PingResult | null = null;
let lastPingAt = 0;
let pingInFlight: Promise<PingResult> | null = null;
let lastConnectResult: ConnectResult | null = null;
let lastConnectAt = 0;
let connectInFlight: Promise<ConnectResult> | null = null;
let connectTarget: string | null = null;
let connectGeneration = 0;
const identityInvalidationListeners = new Set<() => void>();

export function subscribeConnectIdentityInvalidation(listener: () => void): () => void {
  identityInvalidationListeners.add(listener);
  return () => { identityInvalidationListeners.delete(listener); };
}

/** Changes whenever a probe or target supersedes the previously verified identity. */
export function getConnectGeneration(): number {
  return connectGeneration;
}

function invalidateConnectIdentity(): void {
  connectGeneration++;
  lastConnectResult = null;
  lastConnectAt = 0;
  connectInFlight = null;
  connectTarget = getGrpcBaseUrl();
  identityInvalidationListeners.forEach((listener) => listener());
}

subscribeGrpcTargetChange(invalidateConnectIdentity);

function getClient() {
  const transport = getGrpcTransport();
  if (!transport) {
    webLog.warn('revitClient', `getClient: transport unavailable (grpcPort=${getGrpcPort()})`);
    return null;
  }

  return createClient(RevitService, transport);
}

export async function pingRevitGrpc(options: { force?: boolean } = {}): Promise<PingResult> {
  if (!options.force && lastPingResult && Date.now() - lastPingAt < PING_CACHE_TTL_MS) {
    return lastPingResult;
  }

  if (!options.force && pingInFlight) {
    return pingInFlight;
  }

  pingInFlight = pingRevitGrpcUncached().finally(() => {
    pingInFlight = null;
  });

  return pingInFlight;
}

async function pingRevitGrpcUncached(): Promise<PingResult> {
  webLog.debug('revitClient', 'pingRevitGrpc: sending ping...');
  const client = getClient();
  if (!client) {
    const msg = 'Ping: gRPC transport is unavailable';
    webLog.error('revitClient', msg);
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(msg);
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.ping({});
    if (response.isSuccess) {
      webLog.debug('revitClient', 'pingRevitGrpc: success');
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else {
      webLog.warn('revitClient', `pingRevitGrpc: server returned error: ${response.error}`);
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`Ping: ${response.error}`);
    }

    const result = {
      success: response.isSuccess,
      error: response.error || undefined,
    };

    lastPingResult = result;
    lastPingAt = Date.now();
    return result;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Ping gRPC error';
    webLog.error('revitClient', `pingRevitGrpc: failed - ${message}`);
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`Ping: ${message}`);
    const result = {
      success: false,
      error: message,
    };

    lastPingResult = result;
    lastPingAt = Date.now();
    return result;
  }
}

export async function browseForFolderGrpc(
  initialPath = '',
  title = 'Select output folder'
): Promise<{ success: boolean; path?: string; canceled?: boolean; error?: string }> {
  webLog.debug('revitClient', `browseForFolderGrpc: initialPath=${initialPath}`);
  const client = getClient();
  if (!client) {
    const msg = 'BrowseForFolder: gRPC transport is unavailable';
    webLog.error('revitClient', msg);
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.browseForFolder({ initialPath, title });
    if (response.canceled) {
      webLog.info('revitClient', 'browseForFolderGrpc: user canceled');
      return { success: false, canceled: true };
    }
    if (response.isSuccess) {
      webLog.info('revitClient', `browseForFolderGrpc: selected=${response.selectedPath}`);
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
      return { success: true, path: response.selectedPath };
    }
    webLog.warn('revitClient', `browseForFolderGrpc: error=${response.error}`);
    return { success: false, error: response.error || undefined };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'BrowseForFolder gRPC error';
    webLog.error('revitClient', `browseForFolderGrpc: FAILED — ${message}`);
    return { success: false, error: message };
  }
}

export type RevitEventHandler = (eventType: string, payload: string) => void;

interface RevitEventSubscriber {
  onEvent: RevitEventHandler;
  onError?: (err: Error) => void;
}

const revitEventSubscribers = new Set<RevitEventSubscriber>();
let revitEventController: AbortController | null = null;
let revitEventStreamActive = false;

function startRevitEventStream(): void {
  if (revitEventStreamActive) {
    return;
  }

  const client = getClient();
  if (!client) {
    webLog.warn('revitClient', 'subscribeToRevitEvents: gRPC transport unavailable');
    return;
  }

  const controller = new AbortController();
  revitEventController = controller;
  revitEventStreamActive = true;

  (async () => {
    try {
      webLog.debug('revitClient', 'subscribeToRevitEvents: subscribing');
      for await (const evt of client.subscribeToEvents({}, { signal: controller.signal })) {
        const payload = new TextDecoder().decode(evt.payload);
        webLog.debug('revitClient', `subscribeToRevitEvents: ${evt.eventType}`);
        Array.from(revitEventSubscribers).forEach((subscriber) => {
          subscriber.onEvent(evt.eventType, payload);
        });
      }
    } catch (err: any) {
      if (controller.signal.aborted) {
        webLog.debug('revitClient', 'subscribeToRevitEvents: unsubscribed');
        return;
      }

      const message = err instanceof Error ? err.message : String(err);
      webLog.warn('revitClient', `subscribeToRevitEvents: stream error - ${message}`);
      Array.from(revitEventSubscribers).forEach((subscriber) => {
        subscriber.onError?.(err instanceof Error ? err : new Error(message));
      });
    } finally {
      if (revitEventController === controller) {
        revitEventController = null;
      }
      revitEventStreamActive = false;
      if (revitEventSubscribers.size > 0) {
        window.setTimeout(startRevitEventStream, controller.signal.aborted ? 0 : 1000);
      }
    }
  })();
}

/**
 * Subscribe to Revit server-streaming events (document open/close/switch).
 * Returns an abort function to cancel the subscription.
 */
export function subscribeToRevitEvents(
  onEvent: RevitEventHandler,
  onError?: (err: Error) => void,
): () => void {
  const subscriber: RevitEventSubscriber = { onEvent, onError };
  revitEventSubscribers.add(subscriber);
  startRevitEventStream();

  return () => {
    revitEventSubscribers.delete(subscriber);
    if (revitEventSubscribers.size === 0) {
      revitEventController?.abort();
    }
  };
}

export async function connectRevitGrpc(options: { force?: boolean } = {}): Promise<ConnectResult> {
  const target = getGrpcBaseUrl();
  if (target !== connectTarget) invalidateConnectIdentity();
  if (!options.force && lastConnectResult?.success && Date.now() - lastConnectAt < CONNECT_CACHE_TTL_MS) {
    return lastConnectResult;
  }
  if (!options.force && connectInFlight) return connectInFlight;

  invalidateConnectIdentity();
  const generation = connectGeneration;
  const request = connectRevitGrpcUncached().then((result) => {
    if (generation !== connectGeneration || target !== getGrpcBaseUrl()) {
      return { success: false, error: DEVICE_VERIFICATION_ERROR };
    }
    if (result.success) {
      lastConnectResult = result;
      lastConnectAt = Date.now();
    }
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(result.success ? null : `Connect: ${result.error}`);
    return result;
  }).finally(() => {
    if (generation === connectGeneration) connectInFlight = null;
  });
  connectInFlight = request;
  const result = await request;
  return generation === connectGeneration && target === getGrpcBaseUrl()
    ? result
    : { success: false, error: DEVICE_VERIFICATION_ERROR };
}

async function connectRevitGrpcUncached(): Promise<ConnectResult> {
  webLog.debug('revitClient', 'connectRevitGrpc: requesting machine fingerprint');
  const client = getClient();
  if (!client) return { success: false, error: DEVICE_VERIFICATION_ERROR };

  try {
    const response = await client.connect({});
    if (!response.isSuccess) {
      return { success: false, error: DEVICE_VERIFICATION_ERROR };
    }
    const fingerprint = normalizeMachineFingerprint(response.fingerprint);
    if (!fingerprint) return { success: false, error: PLUGIN_UPDATE_ERROR };
    return { success: true, fingerprint };
  } catch {
    webLog.error('revitClient', 'connectRevitGrpc: device verification failed');
    return { success: false, error: DEVICE_VERIFICATION_ERROR };
  }
}

export interface ActiveModelStatus {
  success: boolean;
  error?: string;
  hasActiveDocument: boolean;
  documentTitle: string;
  isReadOnly: boolean;
  isReadOnlyFile: boolean;
  isModifiable: boolean;
  isWorkshared: boolean;
  isDetached: boolean;
  isModified: boolean;
  centralModelPath: string;
  centralModelExists: boolean;
}

// Snapshot of the active Revit document's write-readiness. Pages that
// write parameters (QuickFill, Validation QuickFix) call this on mount
// to short-circuit with a clear banner when:
//   - no document is open (hasActiveDocument=false),
//   - the document is read-only (must be reopened with write access),
//   - the document is workshared but detached (Save won't go to central),
//   - the document is not workshared at all (caller decides whether that
//     blocks the feature or just warns).
// All errors are surfaced via the `error` field rather than thrown — the
// caller usually wants to show the banner, not crash.
export async function getActiveModelStatusGrpc(): Promise<ActiveModelStatus> {
  const empty: ActiveModelStatus = {
    success: false,
    hasActiveDocument: false,
    documentTitle: '',
    isReadOnly: false,
    isReadOnlyFile: false,
    isModifiable: true,
    isWorkshared: false,
    isDetached: false,
    isModified: false,
    centralModelPath: '',
    centralModelExists: false,
  };

  const client = getClient();
  if (!client) {
    webLog.warn('revitClient', 'getActiveModelStatusGrpc: gRPC transport unavailable');
    return { ...empty, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.getActiveModelStatus({});
    // Log every probe so devtools shows the actual flags the plugin returned —
    // crucial when troubleshooting "banner shows ReadOnly even though Revit
    // says it isn't": staleness can come from old plugin DLL (no handler →
    // error string), stale FE state (hot-reload missed), or genuinely RO
    // file. Inspect this line in console first.
    webLog.info(
      'revitClient',
      `getActiveModelStatusGrpc: success=${response.isSuccess} title='${response.documentTitle}' ` +
        `RO=${response.isReadOnly} ROFile=${response.isReadOnlyFile} Modifiable=${response.isModifiable} ` +
        `WS=${response.isWorkshared} Detached=${response.isDetached} ` +
        `CentralPath='${response.centralModelPath}' CentralExists=${response.centralModelExists}`
    );
    if (!response.isSuccess) {
      webLog.warn('revitClient', `getActiveModelStatusGrpc: server error=${response.error}`);
      return { ...empty, error: response.error || 'Server returned failure' };
    }
    return {
      success: true,
      hasActiveDocument: response.hasActiveDocument,
      documentTitle: response.documentTitle,
      isReadOnly: response.isReadOnly,
      isReadOnlyFile: response.isReadOnlyFile,
      isModifiable: response.isModifiable,
      isWorkshared: response.isWorkshared,
      isDetached: response.isDetached,
      isModified: response.isModified,
      centralModelPath: response.centralModelPath,
      centralModelExists: response.centralModelExists,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    webLog.error('revitClient', `getActiveModelStatusGrpc: ${msg}`);
    return { ...empty, error: msg };
  }
}

export interface ProjectPhases {
  success: boolean;
  phaseNames: string[];
  error?: string;
}

export async function getProjectPhasesGrpc(): Promise<ProjectPhases> {
  const empty: ProjectPhases = {
    success: false,
    phaseNames: [],
  };

  const client = getClient();
  if (!client) {
    webLog.warn('revitClient', 'getProjectPhasesGrpc: gRPC transport unavailable');
    return { ...empty, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.getProjectPhases({});
    if (!response.isSuccess) {
      webLog.warn('revitClient', 'getProjectPhasesGrpc: server error=' + response.error);
      return { ...empty, error: response.error || 'Server returned failure' };
    }

    const phaseNames = response.phaseNames
      .map((name) => name.trim())
      .filter((name) => name.length > 0);

    webLog.debug('revitClient', 'getProjectPhasesGrpc: received ' + phaseNames.length + ' phase names');
    return {
      success: true,
      phaseNames,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    webLog.error('revitClient', 'getProjectPhasesGrpc: ' + message);
    return { ...empty, error: message };
  }
}
